import { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  CustomButton,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
  DrawerFormSidebar,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  StatusChip,
} from '../../../common/components';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import {
  ACCOUNTING_DRAWER_WIDTH,
  ACCOUNTING_STATUS_CONFIG,
  ENTRY_DETAIL_SECTIONS,
} from '../constants/accountingEntriesConstants';
import {
  getAccountingOriginLabel,
  humanizeTechnicalValue,
  roundMoney,
  sumAccountingLines,
} from '../utils/accountingEntriesUtils';

const AccountingEntryDetailDrawer = ({ entry, onClose }) => {
  const [activeSection, setActiveSection] = useState('detalle');
  const lines = entry?.lines || [];
  const totals = sumAccountingLines(lines);
  const difference = roundMoney(totals.debit - totals.credit);
  const statusConfig = ACCOUNTING_STATUS_CONFIG[entry?.status] || {
    label: humanizeTechnicalValue(entry?.status),
    color: 'default',
  };

  useEffect(() => {
    if (entry) setActiveSection('detalle');
  }, [entry]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'lineas':
        return (
          <DrawerFormSection title="Líneas contables" grid={false}>
            <GeneralTableBox hasMaxHeight={false}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell sx={{ width: 52 }}>#</HeadTableCell>
                    <HeadTableCell>Cuenta contable</HeadTableCell>
                    <HeadTableCell align="right" sx={{ width: 150, textAlign: 'right' }}>Debe</HeadTableCell>
                    <HeadTableCell align="right" sx={{ width: 150, textAlign: 'right' }}>Haber</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.uuid || line.line_number}>
                      <RowTableCell>{line.line_number}</RowTableCell>
                      <RowTableCell>{line.account_label}</RowTableCell>
                      <RowTableCell align="right" sx={{ textAlign: 'right' }}>
                        {formatCurrency(line.debit_amount, 2)}
                      </RowTableCell>
                      <RowTableCell align="right" sx={{ textAlign: 'right' }}>
                        {formatCurrency(line.credit_amount, 2)}
                      </RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GeneralTableBox>
          </DrawerFormSection>
        );
      case 'totales':
        return (
          <DrawerFormSection title="Totales" grid={false}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              <DrawerFormActionSummary label="Debe" value={formatCurrency(totals.debit, 2)} />
              <DrawerFormActionSummary label="Haber" value={formatCurrency(totals.credit, 2)} />
              <DrawerFormActionSummary label="Diferencia" value={formatCurrency(Math.abs(difference), 2)} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusChip
                  size="small"
                  color={difference === 0 ? 'success' : 'warning'}
                  label={difference === 0 ? 'Balanceado' : 'Con diferencia'}
                />
              </Box>
            </Box>
          </DrawerFormSection>
        );
      case 'detalle':
      default:
        return (
          <DrawerFormSection title="Detalle del asiento" grid={false}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <DrawerFormActionSummary label="Número" value={`#${entry?.entry_number || '-'}`} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusChip size="small" color={statusConfig.color} label={statusConfig.label} />
              </Box>
              <DrawerFormActionSummary label="Fecha" value={formatDate(entry?.entry_date)} />
              <DrawerFormActionSummary
                label="Origen"
                value={getAccountingOriginLabel(entry?.source_type)}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800 }}>
                  Descripción
                </Typography>
                <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                  {entry?.description || '-'}
                </Typography>
              </Box>
            </Box>
          </DrawerFormSection>
        );
    }
  };

  return (
    <DrawerForm
      open={Boolean(entry)}
      onClose={onClose}
      title="Detalle del asiento"
      width={ACCOUNTING_DRAWER_WIDTH}
      sidebar={(
        <DrawerFormSidebar
          sections={ENTRY_DETAIL_SECTIONS}
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
      )}
      footer={(
        <DrawerFormActions
          summary={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <DrawerFormActionSummary label="Debe" value={formatCurrency(totals.debit, 2)} />
              <DrawerFormActionSummary label="Haber" value={formatCurrency(totals.credit, 2)} />
            </Box>
          )}
        >
          <CustomButton variant="outlined" onClick={onClose}>Cerrar</CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.25 }, minWidth: 0 }}>
        {renderActiveSection()}
      </Box>
    </DrawerForm>
  );
};

export default AccountingEntryDetailDrawer;
